import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  Field,
  Checkbox,
  RadioGroup,
  Radio,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  ArrowRight24Regular,
  Save24Regular,
  Delete24Regular,
  Add24Regular,
} from "@fluentui/react-icons";

import { useProposalApi } from "./useProposalApi";
import type { Salesperson } from "./useProposalApi";
import type {
  Proposal,
  ProposalModel,
  LineItem,
  PaymentPeriod,
  DiamondClubMember,
} from "./types";
import { US_STATES, SERVICE_TYPE_OPTIONS, CONTACT_TITLE_OPTIONS } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  stepIndicator: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  stepDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: tokens.colorNeutralStroke1,
  },
  stepDotActive: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: tokens.colorBrandBackground,
  },
  form: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    maxWidth: "700px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  lineItemRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  lineItemField: {
    flex: 1,
  },
});

const STEPS = ["Agency Details", "Services & Term", "Line Items", "References", "Review"];

const MIN_REFERENCES = 3;

interface ProposalWizardProps {
  existingProposal?: Proposal | null;
  onBack: () => void;
  onSaved: (proposal: Proposal) => void;
}

function emptyModel(): ProposalModel {
  return {
    agency_name: "",
    agency_address: "",
    agency_contact_name: "",
    agency_contact_title: "",
    agency_contact_first_name: "",
    agency_contact_last_name: "",
    agency_state: "",
    service_types: [],
    term_months: 60,
    salesperson_name: "",
    salesperson_title: "Account Executive",
    salesperson_phone: "",
    salesperson_email: "",
    line_items: [{ quantity: 1, component: "", description: "" }],
    total_investment_amount: 0,
    target_start_date: "TBD",
    equal_payments: true,
    payment_periods: [],
    selected_references: [],
    proposal_date: new Date().toISOString().slice(0, 10),
    proposal_id: "",
  };
}

/** Round to cents for tolerant float comparison. */
function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Format a dollar amount with cents always shown (e.g. $1,200.00). */
function formatUSD(n: number): string {
  return `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Backfill new fields on proposals created before payment scheduling. */
function normalizeModel(m: ProposalModel): ProposalModel {
  return {
    ...m,
    target_start_date: m.target_start_date || "TBD",
    equal_payments: m.equal_payments ?? true,
    payment_periods: m.payment_periods ?? [],
    selected_references: m.selected_references ?? [],
  };
}

/** Today's date as ISO yyyy-mm-dd (local). */
function todayIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

export const ProposalWizard: React.FC<ProposalWizardProps> = ({
  existingProposal,
  onBack,
  onSaved,
}) => {
  const styles = useStyles();
  const proposalApi = useProposalApi();

  const [step, setStep] = useState(0);
  const [model, setModel] = useState<ProposalModel>(
    existingProposal ? normalizeModel(existingProposal.model) : emptyModel(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salespersons, setSalespersons] = useState<Salesperson[]>([]);
  const [diamondClub, setDiamondClub] = useState<DiamondClubMember[]>([]);
  const referencesInitialized = useRef(false);

  useEffect(() => {
    proposalApi.listSalespersons().then(setSalespersons).catch(() => {});
    proposalApi.listDiamondClub().then(setDiamondClub).catch(() => {});
  }, [proposalApi]);

  // For brand-new proposals, pre-select the default ("Y") reference agencies
  // once the Diamond Club list loads. Existing proposals keep their saved set.
  useEffect(() => {
    if (referencesInitialized.current) return;
    if (existingProposal) {
      referencesInitialized.current = true;
      return;
    }
    if (diamondClub.length && model.selected_references.length === 0) {
      const defaults = diamondClub
        .filter((m) => m.default === "Y")
        .map((m) => ({
          agency_name: m.agency_name,
          contact_name: m.contact_name,
          contact_phone: m.contact_phone,
        }));
      if (defaults.length) {
        setModel((prev) => ({ ...prev, selected_references: defaults }));
        referencesInitialized.current = true;
      }
    }
  }, [diamondClub, existingProposal, model.selected_references.length]);

  const toggleReference = useCallback((m: DiamondClubMember, checked: boolean) => {
    setModel((prev) => {
      const exists = prev.selected_references.some(
        (r) => r.agency_name === m.agency_name,
      );
      let refs = prev.selected_references;
      if (checked && !exists) {
        refs = [
          ...refs,
          {
            agency_name: m.agency_name,
            contact_name: m.contact_name,
            contact_phone: m.contact_phone,
          },
        ];
      } else if (!checked && exists) {
        refs = refs.filter((r) => r.agency_name !== m.agency_name);
      }
      return { ...prev, selected_references: refs };
    });
  }, []);

  const patch = useCallback(
    (updates: Partial<ProposalModel>) => {
      setModel((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const updateLineItem = useCallback(
    (idx: number, updates: Partial<LineItem>) => {
      setModel((prev) => {
        const items = [...prev.line_items];
        items[idx] = { ...items[idx], ...updates };
        return { ...prev, line_items: items };
      });
    },
    [],
  );

  const addLineItem = useCallback(() => {
    setModel((prev) => ({
      ...prev,
      line_items: [...prev.line_items, { quantity: 1, component: "", description: "" }],
    }));
  }, []);

  const removeLineItem = useCallback((idx: number) => {
    setModel((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx),
    }));
  }, []);

  const addPaymentPeriod = useCallback(() => {
    setAmountDrafts({});
    setModel((prev) => ({
      ...prev,
      payment_periods: [...prev.payment_periods, { start_date: "", amount: 0 }],
    }));
  }, []);

  const updatePaymentPeriod = useCallback(
    (idx: number, updates: Partial<PaymentPeriod>) => {
      setModel((prev) => {
        const periods = [...prev.payment_periods];
        periods[idx] = { ...periods[idx], ...updates };
        return { ...prev, payment_periods: periods };
      });
    },
    [],
  );

  const removePaymentPeriod = useCallback((idx: number) => {
    setAmountDrafts({});
    setModel((prev) => ({
      ...prev,
      payment_periods: prev.payment_periods.filter((_, i) => i !== idx),
    }));
  }, []);

  // Raw text drafts for currency inputs. A number-backed value reformats an
  // in-progress decimal like "18467." to "18467", so the next keystroke ("0")
  // produces "184670". Keeping the raw string preserves the trailing dot while
  // still pushing a parsed number into the model.
  const [amountDrafts, setAmountDrafts] = useState<Record<string, string>>({});
  const setAmountDraft = useCallback((key: string, raw: string): number => {
    let v = raw.replace(/[^\d.]/g, "");
    const dot = v.indexOf(".");
    if (dot !== -1) {
      v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
    }
    setAmountDrafts((m) => ({ ...m, [key]: v }));
    return parseFloat(v) || 0;
  }, []);

  const today = todayIso();
  const startIsTbd = !model.target_start_date || model.target_start_date === "TBD";
  const targetDateInvalid = !startIsTbd && model.target_start_date < today;

  const paymentsSum = roundCents(
    model.payment_periods.reduce((acc, p) => acc + (Number(p.amount) || 0), 0),
  );
  const paymentsBalance = roundCents(model.total_investment_amount - paymentsSum);
  // Custom schedule is valid only when every period has a date + positive
  // amount AND the amounts sum exactly to the contract value.
  const customScheduleValid =
    model.equal_payments ||
    (model.payment_periods.length > 0 &&
      model.payment_periods.every((p) => p.start_date && p.amount > 0) &&
      paymentsBalance === 0);

  const canAdvance = (): boolean => {
    switch (step) {
      case 0:
        return !!(
          model.agency_name &&
          model.agency_address &&
          model.agency_contact_title &&
          model.agency_contact_first_name &&
          model.agency_contact_last_name &&
          model.agency_state
        );
      case 1:
        return (
          model.service_types.length > 0 &&
          model.term_months >= 12 &&
          !!model.salesperson_name &&
          !targetDateInvalid
        );
      case 2:
        return (
          model.line_items.length > 0 &&
          model.line_items.every((li) => li.quantity > 0 && li.component) &&
          model.total_investment_amount > 0 &&
          customScheduleValid
        );
      case 3:
        return model.selected_references.length >= MIN_REFERENCES;
      default:
        return true;
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      let proposal: Proposal;
      if (existingProposal) {
        proposal = await proposalApi.update(existingProposal.id, model);
      } else {
        proposal = await proposalApi.create(model);
      }
      onSaved(proposal);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            <Text size={500} weight="semibold">Agency Details</Text>
            <Field
              label="Agency Name"
              required
              hint="Shown exactly as entered on the cover page (e.g. &quot;Sherman PD - TX&quot;)."
            >
              <Input
                value={model.agency_name}
                onChange={(_, d) => patch({ agency_name: d.value })}
                placeholder="e.g. Springfield Police Department"
              />
            </Field>
            <Field label="Agency Address" required>
              <Input
                value={model.agency_address}
                onChange={(_, d) => patch({ agency_address: d.value })}
                placeholder="123 Main St, Springfield, IL 62701"
              />
            </Field>
            <Field
              label="Contact Title"
              required
              hint={`Used in greeting (e.g. "${
                model.agency_contact_title || "Chief"
              } ${model.agency_contact_last_name || "Doe"},"). Common: ${CONTACT_TITLE_OPTIONS.slice(0, 5).join(", ")}…`}
            >
              <Input
                value={model.agency_contact_title}
                onChange={(_, d) => patch({ agency_contact_title: d.value })}
                placeholder="Chief"
                list="contact-title-suggestions"
              />
            </Field>
            <datalist id="contact-title-suggestions">
              {CONTACT_TITLE_OPTIONS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <div style={{ display: "flex", gap: tokens.spacingHorizontalM }}>
              <Field label="Contact First Name" required style={{ flex: 1 }}>
                <Input
                  value={model.agency_contact_first_name}
                  onChange={(_, d) => patch({ agency_contact_first_name: d.value })}
                  placeholder="Jane"
                />
              </Field>
              <Field label="Contact Last Name" required style={{ flex: 1 }}>
                <Input
                  value={model.agency_contact_last_name}
                  onChange={(_, d) => patch({ agency_contact_last_name: d.value })}
                  placeholder="Doe"
                />
              </Field>
            </div>
            <Field label="State" required>
              <Dropdown
                value={model.agency_state}
                selectedOptions={model.agency_state ? [model.agency_state] : []}
                onOptionSelect={(_, d) => patch({ agency_state: d.optionValue || "" })}
                placeholder="Select state"
              >
                {US_STATES.map((s) => (
                  <Option key={s} value={s}>{s}</Option>
                ))}
              </Dropdown>
            </Field>
          </>
        );
      case 1:
        return (
          <>
            <Text size={500} weight="semibold">Services & Term</Text>
            <Field label="Service Types" required>
              <Dropdown
                multiselect
                value={model.service_types.map((s) => {
                  const found = SERVICE_TYPE_OPTIONS.find((o) => o.value === s);
                  return found?.label || s;
                }).join(", ")}
                selectedOptions={model.service_types}
                onOptionSelect={(_, d) => {
                  const selected = d.selectedOptions as string[];
                  patch({ service_types: selected });
                }}
                placeholder="Select service types"
              >
                {SERVICE_TYPE_OPTIONS.map((o) => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Dropdown>
            </Field>
            <Field label="Deal Term (months)" hint="Drives warranty period, annual price, and term references throughout the proposal.">
              <Input
                type="number"
                value={String(model.term_months)}
                onChange={(_, d) => patch({ term_months: parseInt(d.value) || 60 })}
                min="12"
                max="120"
              />
            </Field>
            <Field label="Salesperson" required>
              <Dropdown
                value={model.salesperson_name || ""}
                selectedOptions={model.salesperson_name ? [model.salesperson_name] : []}
                onOptionSelect={(_, d) => {
                  const sp = salespersons.find((s) => s.display_name === d.optionValue);
                  if (sp) {
                    patch({
                      salesperson_name: sp.display_name,
                      salesperson_title: sp.title || "Account Executive",
                      salesperson_phone: sp.phone || "",
                      salesperson_email: sp.email || "",
                    });
                  }
                }}
                placeholder={salespersons.length ? "Select a salesperson" : "Loading…"}
              >
                {salespersons.map((sp) => (
                  <Option key={sp.id} value={sp.display_name}>
                    {sp.display_name}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {model.salesperson_name && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: tokens.spacingHorizontalL, paddingLeft: tokens.spacingHorizontalM }}>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {model.salesperson_title}
                </Text>
                {model.salesperson_phone && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {model.salesperson_phone}
                  </Text>
                )}
                {model.salesperson_email && (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {model.salesperson_email}
                  </Text>
                )}
              </div>
            )}
            <Field label="Proposal Date">
              <Input
                type="date"
                value={model.proposal_date}
                onChange={(_, d) => patch({ proposal_date: d.value })}
              />
            </Field>
            <Field label="Target Start Date">
              <RadioGroup
                layout="horizontal"
                value={startIsTbd ? "tbd" : "date"}
                onChange={(_, d) =>
                  patch({ target_start_date: d.value === "tbd" ? "TBD" : today })
                }
              >
                <Radio value="tbd" label="To be determined" />
                <Radio value="date" label="Specific date" />
              </RadioGroup>
            </Field>
            {!startIsTbd && (
              <Field
                validationState={targetDateInvalid ? "error" : "none"}
                validationMessage={
                  targetDateInvalid ? "Please choose today or a future date." : undefined
                }
              >
                <Input
                  type="date"
                  value={model.target_start_date}
                  min={today}
                  onChange={(_, d) => patch({ target_start_date: d.value })}
                />
              </Field>
            )}
          </>
        );
      case 2:
        return (
          <>
            <Text size={500} weight="semibold">Line Items</Text>
            {model.line_items.map((li, idx) => (
              <div key={idx} className={styles.lineItemRow}>
                <Field label={idx === 0 ? "Qty" : undefined} className={styles.lineItemField} style={{ maxWidth: "80px" }}>
                  <Input
                    type="number"
                    value={String(li.quantity)}
                    onChange={(_, d) => updateLineItem(idx, { quantity: parseInt(d.value) || 0 })}
                    min="1"
                  />
                </Field>
                <Field label={idx === 0 ? "Component" : undefined} className={styles.lineItemField}>
                  <Input
                    value={li.component}
                    onChange={(_, d) => updateLineItem(idx, { component: d.value })}
                    placeholder="e.g. L4 Body Camera"
                  />
                </Field>
                <Field label={idx === 0 ? "Description" : undefined} className={styles.lineItemField}>
                  <Input
                    value={li.description || ""}
                    onChange={(_, d) => updateLineItem(idx, { description: d.value })}
                    placeholder="Optional"
                  />
                </Field>
                {model.line_items.length > 1 && (
                  <Button
                    appearance="subtle"
                    icon={<Delete24Regular />}
                    size="small"
                    onClick={() => removeLineItem(idx)}
                    style={{ marginBottom: idx === 0 ? 0 : undefined }}
                  />
                )}
              </div>
            ))}
            <Button
              appearance="outline"
              icon={<Add24Regular />}
              size="small"
              onClick={addLineItem}
            >
              Add Line Item
            </Button>
            <Field label="Contract Value ($)" required>
              <Input
                type="text"
                inputMode="decimal"
                value={
                  amountDrafts["contract"] ??
                  (model.total_investment_amount ? String(model.total_investment_amount) : "")
                }
                onChange={(_, d) =>
                  patch({ total_investment_amount: setAmountDraft("contract", d.value) })
                }
                placeholder="250000"
              />
            </Field>

            <Checkbox
              checked={model.equal_payments}
              onChange={(_, d) =>
                patch({
                  equal_payments: !!d.checked,
                  // Seed one empty period when switching to a custom schedule.
                  payment_periods:
                    !d.checked && model.payment_periods.length === 0
                      ? [{ start_date: model.proposal_date, amount: 0 }]
                      : model.payment_periods,
                })
              }
              label="Equal Payments Every Year"
            />

            {!model.equal_payments && (
              <>
                <Text size={300} weight="semibold">Payment Schedule</Text>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  Enter each payment period’s start date and amount. The amounts
                  must add up to the Contract Value above.
                </Text>
                {model.payment_periods.map((p, idx) => (
                  <div key={idx} className={styles.lineItemRow}>
                    <Field label={idx === 0 ? "Start Date" : undefined} className={styles.lineItemField}>
                      <Input
                        type="date"
                        value={p.start_date}
                        onChange={(_, d) => updatePaymentPeriod(idx, { start_date: d.value })}
                      />
                    </Field>
                    <Field label={idx === 0 ? "Amount ($)" : undefined} className={styles.lineItemField}>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={
                          amountDrafts[`p${idx}`] ??
                          (p.amount ? String(p.amount) : "")
                        }
                        onChange={(_, d) =>
                          updatePaymentPeriod(idx, {
                            amount: setAmountDraft(`p${idx}`, d.value),
                          })
                        }
                        placeholder="50000"
                      />
                    </Field>
                    {model.payment_periods.length > 1 && (
                      <Button
                        appearance="subtle"
                        icon={<Delete24Regular />}
                        size="small"
                        onClick={() => removePaymentPeriod(idx)}
                        style={{ marginBottom: idx === 0 ? 0 : undefined }}
                      />
                    )}
                  </div>
                ))}
                <Button
                  appearance="outline"
                  icon={<Add24Regular />}
                  size="small"
                  onClick={addPaymentPeriod}
                >
                  Add Payment Period
                </Button>
                {model.total_investment_amount > 0 && (
                  <MessageBar intent={paymentsBalance === 0 ? "success" : "warning"}>
                    <MessageBarBody>
                      {paymentsBalance === 0
                        ? `Scheduled payments total ${formatUSD(paymentsSum)}, matching the contract value.`
                        : paymentsBalance > 0
                          ? `Scheduled payments total ${formatUSD(paymentsSum)} — ${formatUSD(paymentsBalance)} short of the ${formatUSD(model.total_investment_amount)} contract value.`
                          : `Scheduled payments total ${formatUSD(paymentsSum)} — ${formatUSD(Math.abs(paymentsBalance))} over the ${formatUSD(model.total_investment_amount)} contract value.`}
                    </MessageBarBody>
                  </MessageBar>
                )}
              </>
            )}
          </>
        );
      case 3:
        return (
          <>
            <Text size={500} weight="semibold">Reference Agencies</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              Select the LensLock Diamond Club agencies to feature in Section VI.
              At least {MIN_REFERENCES} must be selected to continue.
            </Text>
            {diamondClub.length === 0 ? (
              <Spinner label="Loading reference agencies…" />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS }}>
                {diamondClub.map((m) => {
                  const checked = model.selected_references.some(
                    (r) => r.agency_name === m.agency_name,
                  );
                  return (
                    <Checkbox
                      key={m.id}
                      checked={checked}
                      onChange={(_, d) => toggleReference(m, !!d.checked)}
                      label={`${m.agency_name} — ${m.contact_name}`}
                    />
                  );
                })}
              </div>
            )}
            {model.selected_references.length < MIN_REFERENCES && (
              <MessageBar intent="warning">
                <MessageBarBody>
                  Select at least {MIN_REFERENCES} reference agencies to continue —
                  you have {model.selected_references.length} selected
                  {model.selected_references.length > 0
                    ? ` (${MIN_REFERENCES - model.selected_references.length} more needed).`
                    : "."}
                </MessageBarBody>
              </MessageBar>
            )}
          </>
        );
      case 4:
        return (
          <>
            <Text size={500} weight="semibold">Review</Text>
            <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
              <Text><strong>Agency:</strong> {model.agency_name}</Text>
              <Text><strong>Address:</strong> {model.agency_address}</Text>
              <Text>
                <strong>Contact:</strong>{" "}
                {[
                  model.agency_contact_title,
                  model.agency_contact_first_name,
                  model.agency_contact_last_name,
                ].filter(Boolean).join(" ") || model.agency_contact_name}
              </Text>
              <Text><strong>State:</strong> {model.agency_state}</Text>
              <Text><strong>Services:</strong> {model.service_types.join(", ")}</Text>
              <Text><strong>Deal Term:</strong> {model.term_months} months</Text>
              <Text>
                <strong>Target Start Date:</strong>{" "}
                {startIsTbd ? "To be determined" : model.target_start_date}
              </Text>
              <Text>
                <strong>Salesperson:</strong>{" "}
                {model.salesperson_name}
                {model.salesperson_title ? `, ${model.salesperson_title}` : ""}
                {model.salesperson_email ? ` — ${model.salesperson_email}` : ""}
              </Text>
              <Text><strong>Contract Value:</strong> {formatUSD(model.total_investment_amount)}</Text>
              <Text>
                <strong>Payment Schedule:</strong>{" "}
                {model.equal_payments ? "Equal payments every year" : "Custom schedule"}
              </Text>
              {!model.equal_payments &&
                model.payment_periods.map((p, idx) => (
                  <Text key={idx} style={{ paddingLeft: tokens.spacingHorizontalL }}>
                    {p.start_date || "(no date)"} — {formatUSD(p.amount)}
                  </Text>
                ))}
              <Text><strong>Line Items:</strong> {model.line_items.length}</Text>
              {model.line_items.map((li, idx) => (
                <Text key={idx} style={{ paddingLeft: tokens.spacingHorizontalL }}>
                  {li.quantity}x {li.component}{li.description ? ` — ${li.description}` : ""}
                </Text>
              ))}
              <Text><strong>Reference Agencies:</strong> {model.selected_references.length}</Text>
              {model.selected_references.map((r, idx) => (
                <Text key={idx} style={{ paddingLeft: tokens.spacingHorizontalL }}>
                  {r.agency_name}{r.contact_name ? ` — ${r.contact_name}` : ""}
                </Text>
              ))}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack}>
          Back
        </Button>
        <Text size={400} weight="semibold">
          {existingProposal ? "Edit Proposal" : "New Proposal"}
        </Text>
      </div>

      <div className={styles.stepIndicator}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={i <= step ? styles.stepDotActive : styles.stepDot} />
            <Text
              size={200}
              weight={i === step ? "semibold" : "regular"}
              style={{ color: i === step ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3 }}
            >
              {s}
            </Text>
          </React.Fragment>
        ))}
      </div>

      {error && (
        <MessageBar intent="error" style={{ margin: tokens.spacingHorizontalM }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.form}>{renderStep()}</div>

      <div className={styles.footer}>
        <Button
          appearance="outline"
          icon={<ArrowLeft24Regular />}
          disabled={step === 0}
          onClick={() => setStep((s) => s - 1)}
        >
          Previous
        </Button>
        <div style={{ display: "flex", gap: tokens.spacingHorizontalS }}>
          <Button
            appearance="outline"
            icon={<Save24Regular />}
            onClick={saveDraft}
            disabled={saving}
          >
            {saving ? <Spinner size="tiny" /> : "Save Draft"}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              appearance="primary"
              icon={<ArrowRight24Regular />}
              iconPosition="after"
              disabled={!canAdvance()}
              onClick={() => setStep((s) => s + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              appearance="primary"
              icon={<Save24Regular />}
              disabled={saving || !canAdvance()}
              onClick={saveDraft}
            >
              {saving ? <Spinner size="tiny" /> : "Save & Continue"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
